import React, { useState } from 'react';
import { Review } from '../types/Review';
import { format } from 'date-fns';
import { 
  HighlightCountBadge, 
  PriorityBadge, 
  SourceBadge,
  ImpactScoreBadge 
} from './ReviewBadges';
import '../styles/HighlightsOnlyView.css';

interface HighlightsOnlyViewProps {
  reviews: Review[];
}

const HighlightsOnlyView: React.FC<HighlightsOnlyViewProps> = ({ reviews }) => {
  const [sortBy, setSortBy] = useState<'priority' | 'impact' | 'date' | 'quality'>('priority');
  const [groupBy, setGroupBy] = useState<'category' | 'angle' | 'priority' | 'none'>('category');

  // Extract all highlights from reviews
  const allHighlights = reviews.flatMap(review => 
    (review.highlights || []).map(highlight => ({
      ...highlight,
      reviewId: review.id,
      reviewerName: review.reviewer_name,
      reviewDate: review.timestamp,
      source: review.source,
      rating: review.rating,
      priority: review.metadata?.priority,
      impactScore: review.metadata?.impact_score,
      reviewSnippet: review.review.substring(0, 100) + '...'
    }))
  );

  // Sort highlights
  const sortedHighlights = [...allHighlights].sort((a, b) => {
    switch (sortBy) {
      case 'priority':
        const priorityOrder = { 'GOLD': 3, 'SILVER': 2, 'BRONZE': 1 };
        return (priorityOrder[b.priority as keyof typeof priorityOrder] || 0) - 
               (priorityOrder[a.priority as keyof typeof priorityOrder] || 0);
      case 'impact':
        return (b.impactScore || 0) - (a.impactScore || 0);
      case 'quality':
        return (b.quality || 0) - (a.quality || 0);
      case 'date':
        return new Date(b.reviewDate).getTime() - new Date(a.reviewDate).getTime();
      default:
        return 0;
    }
  });

  // Group highlights
  const groupedHighlights = groupBy === 'none' ? 
    { 'All Highlights': sortedHighlights } :
    sortedHighlights.reduce((groups, highlight) => {
      let key = 'Other';
      
      switch (groupBy) {
        case 'category':
          key = highlight.category || 'Uncategorized';
          break;
        case 'angle':
          key = highlight.angle || 'No Angle';
          break;
        case 'priority':
          key = highlight.priority || 'No Priority';
          break;
      }
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(highlight);
      return groups;
    }, {} as Record<string, typeof sortedHighlights>);

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return 'N/A';
    }
  };

  const getQualityColor = (quality: number) => {
    if (quality >= 4) return 'quality-high';
    if (quality >= 3) return 'quality-medium';
    return 'quality-low';
  };

  const copyHighlight = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  return (
    <div className="highlights-only-view">
      <div className="highlights-header">
        <div className="view-title">
          <h2>Marketing Highlights</h2>
          <p>{allHighlights.length} key quotes from {reviews.length} reviews</p>
        </div>
        
        <div className="highlights-controls">
          <div className="control-group">
            <label>Sort by:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
              <option value="priority">Priority</option>
              <option value="impact">Impact Score</option>
              <option value="quality">Quality Score</option>
              <option value="date">Date</option>
            </select>
          </div>
          
          <div className="control-group">
            <label>Group by:</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as any)}>
              <option value="none">No Grouping</option>
              <option value="category">Category</option>
              <option value="angle">Angle</option>
              <option value="priority">Priority</option>
            </select>
          </div>
        </div>
      </div>

      <div className="highlights-content">
        {Object.entries(groupedHighlights).map(([groupName, highlights]) => (
          <div key={groupName} className="highlight-group">
            {groupBy !== 'none' && (
              <div className="group-header">
                <h3>{groupName}</h3>
                <span className="group-count">{highlights.length} highlights</span>
              </div>
            )}
            
            <div className="highlights-grid">
              {highlights.map((highlight, index) => (
                <div key={`${highlight.reviewId}-${index}`} className="highlight-card">
                  <div className="highlight-content">
                    <div className="highlight-text" onClick={() => copyHighlight(highlight.text)}>
                      "{highlight.text}"
                    </div>
                    
                    <div className="highlight-meta">
                      <div className="meta-row">
                        <SourceBadge source={highlight.source} />
                        <span className="reviewer">by {highlight.reviewerName || 'Anonymous'}</span>
                        <span className="date">{formatDate(highlight.reviewDate)}</span>
                      </div>
                      
                      <div className="meta-row">
                        {highlight.priority && <PriorityBadge priority={highlight.priority} />}
                        {highlight.impactScore && <ImpactScoreBadge score={highlight.impactScore} />}
                        <span className={`quality-badge ${getQualityColor(highlight.quality || 0)}`}>
                          Quality: {highlight.quality || 0}/5
                        </span>
                      </div>
                      
                      {highlight.category && (
                        <div className="meta-row">
                          <span className="category-tag">{highlight.category}</span>
                          {highlight.angle && <span className="angle-tag">{highlight.angle}</span>}
                        </div>
                      )}
                    </div>
                    
                    <div className="review-context">
                      <div className="context-header">
                        <span className="rating">{'★'.repeat(highlight.rating)}</span>
                        <button 
                          className="copy-btn"
                          onClick={() => copyHighlight(highlight.text)}
                          title="Copy highlight"
                        >
                          📋
                        </button>
                      </div>
                      <p className="review-snippet">{highlight.reviewSnippet}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {allHighlights.length === 0 && (
        <div className="empty-highlights">
          <div className="empty-icon">💬</div>
          <h3>No highlights found</h3>
          <p>Reviews need to be processed to extract marketing highlights</p>
        </div>
      )}
    </div>
  );
};

export default HighlightsOnlyView;